import type { AppUser } from '@protoss/shared';
import {
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
  type ActionCodeSettings,
  type User,
} from 'firebase/auth';
import * as React from 'react';
import { firebaseAuth, googleProvider } from '../lib/firebase';
import { getPreAuthConfig } from '../lib/preAuthConfig';
import { withDevWorkspaceParam } from '../lib/url';

/**
 * Normalized Firebase auth user shape
 *
 * Provides a stable interface that pages and components can depend on,
 * independent of the Firebase User object internals.
 */
export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
}

/**
 * Shared auth context contract
 *
 * All auth actions go through this interface so page components
 * never depend on the underlying auth provider directly.
 */
export interface AuthContextValue {
  /** Firebase user (identity layer) */
  user: AuthUser | null;
  /** Application user from Postgres (source of truth) */
  appUser: AppUser | null;
  isAuthenticated: boolean;
  /** True until the auth session has been resolved on app load */
  isLoading: boolean;

  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUpWithPassword: (email: string, password: string, displayName: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  resetPassword: (oobCode: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-send the Firebase email verification to the currently signed-in user. */
  resendVerificationEmail: () => Promise<void>;
  /**
   * Apply a Firebase email verification action code, reload the user, and
   * synchronise the verified state into the application database.
   */
  verifyEmail: (oobCode: string) => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

function mapFirebaseUser(firebaseUser: User): AuthUser {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    emailVerified: firebaseUser.emailVerified,
  };
}

/**
 * Exchange a Firebase ID token for the application user record.
 *
 * Calls POST /api/auth/firebase - the backend verifies the token,
 * finds or creates the user in Postgres, and returns the app user.
 *
 * Returns null on any failure (network error, suspended account, etc.)
 * and signs out of Firebase when the backend rejects the token.
 */
async function exchangeIdToken(firebaseUser: User, forceRefresh = false): Promise<AppUser | null> {
  let idToken: string;
  try {
    idToken = await firebaseUser.getIdToken(forceRefresh);
  } catch {
    throw new Error('TOKEN_EXCHANGE_FAILED');
  }

  const { workspaceSlug } = getPreAuthConfig();
  const response = await fetch('/api/auth/firebase', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, ...(workspaceSlug ? { workspaceSlug } : {}) }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
    if (response.status === 401 || response.status === 403) {
      await firebaseSignOut(firebaseAuth).catch(() => undefined);
    }

    throw new Error(body.error ?? body.message ?? `AUTH_EXCHANGE_FAILED:${response.status}`);
  }

  const data = (await response.json()) as { user: AppUser };
  return data.user;
}

/**
 * Synchronise Firebase verification state into the Protoss database.
 *
 * Calls POST /api/auth/firebase/sync with the token in the Authorization
 * header. Used after email verification so the DB reflects email_verified=true.
 * Returns null on any failure without signing the user out.
 */
async function syncVerifiedState(firebaseUser: User): Promise<AppUser | null> {
  let idToken: string;
  try {
    // Force-refresh ensures the new token carries email_verified=true
    idToken = await firebaseUser.getIdToken(true);
  } catch (err) {
    console.error('[auth] syncVerifiedState: getIdToken failed', err);
    return null;
  }

  try {
    const response = await fetch('/api/auth/firebase/sync', {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
    });

    if (!response.ok) {
      console.error(
        '[auth] syncVerifiedState: sync endpoint returned',
        response.status,
        await response.text().catch(() => '')
      );
      return null;
    }

    const data = (await response.json()) as { user: AppUser };
    return data.user;
  } catch (err) {
    console.error('[auth] syncVerifiedState: fetch failed', err);
    return null;
  }
}

/**
 * AuthProvider
 *
 * Wraps the application and provides auth state and actions via context.
 * Place this at the root of the tree, outside of the router.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [appUser, setAppUser] = React.useState<AppUser | null>(null);
  // Latest user mirrored into a ref so the visibility-change effect (deps: [])
  // can compare against current state without re-subscribing or stale closures.
  const userRef = React.useRef(user);
  userRef.current = user;
  // isLoading starts true so protected routes don't flicker before the
  // first onAuthStateChanged callback fires.
  const [isLoading, setIsLoading] = React.useState(true);

  // Tracks whether a programmatic sign-in action (signInWithGoogle,
  // signInWithPassword, signUpWithPassword) is in flight. When true, the
  // onAuthStateChanged listener skips its own exchangeIdToken call and
  // setIsLoading(true) so it does NOT unmount the SignInPage mid-flow.
  // Without this guard, PublicRoute returns null (unmounts SignInPage) the
  // moment Firebase fires onAuthStateChanged after signInWithPopup, which
  // causes any error state set in the sign-in action's catch block to be
  // lost on remount.
  const isSignInActionPending = React.useRef(false);

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
      if (firebaseUser) {
        // A programmatic sign-in action is already handling exchangeIdToken
        // and state updates. Skip this callback to avoid a double call and
        // the setIsLoading(true) that would unmount the SignInPage.
        if (isSignInActionPending.current) {
          return;
        }
        // Reset isLoading to true before updating user state so that
        // WorkspaceContext's isAuthLoading guard stays active during the
        // exchangeIdToken call. Without this, if the user signs in after
        // the initial null-user event has already set isLoading=false,
        // WorkspaceContext fires fetchModules immediately - before POST
        // /api/auth/firebase has run - causing authenticate() to fail with
        // USER_NOT_FOUND (firebaseUid not yet written to platform.user).
        setIsLoading(true);
        setUser(mapFirebaseUser(firebaseUser));
        // Force-refresh the token when Firebase reports the email as verified.
        // applyActionCode updates the local user object (emailVerified=true) but
        // does not refresh the cached JWT - so getIdToken(false) would still
        // return a token where email_verified=false in its claims. That stale
        // token reaches the backend and prevents the DB from being updated.
        // Force-refreshing here ensures the token claims match Firebase state.
        try {
          const resolvedAppUser = await exchangeIdToken(firebaseUser, firebaseUser.emailVerified);
          setAppUser(resolvedAppUser);
        } catch {
          setAppUser(null);
        }
      } else {
        setUser(null);
        setAppUser(null);
      }
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  // Re-sync the app user when the tab becomes visible again after a period of
  // inactivity. Firebase silently refreshes ID tokens every ~1 hour without
  // re-triggering onAuthStateChanged, so the Google profile photo URL
  // (picture claim) can change while the React state still holds the stale
  // avatarUrl from the initial page load - causing the avatar to stop rendering.
  React.useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      const currentUser = firebaseAuth.currentUser;
      if (!currentUser) return;
      await currentUser.reload();
      // Only disturb React state when the profile actually changed. Firing setUser/
      // exchangeIdToken on EVERY tab focus recreates the (non-memoized) auth context
      // value, which re-renders all consumers and unmounts auth-gated subtrees - e.g.
      // it tears down the AI chat WebSocket mid-run (the cleanup calls disconnect(),
      // and the clean teardown suppresses auto-reconnect). reload() above is cheap and
      // local; the state churn is what breaks in-flight runs.
      const mapped = mapFirebaseUser(currentUser);
      const prev = userRef.current;
      const unchanged =
        prev != null &&
        prev.uid === mapped.uid &&
        prev.email === mapped.email &&
        prev.displayName === mapped.displayName &&
        prev.photoURL === mapped.photoURL &&
        prev.emailVerified === mapped.emailVerified;
      if (unchanged) return;
      setUser(mapped);
      // Force-refresh the token so the `picture` claim reflects the user's
      // current Google profile photo. Without this, a cached JWT (valid for up
      // to 1 hour) can carry a stale picture URL - when the backend receives it,
      // it overwrites the DB avatarUrl with the old URL, undoing any Google photo
      // change the user made since the last full page load.
      try {
        const refreshedAppUser = await exchangeIdToken(currentUser, true);
        if (refreshedAppUser) setAppUser(refreshedAppUser);
      } catch {
        setAppUser(null);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const signInWithGoogle = async (): Promise<void> => {
    isSignInActionPending.current = true;
    try {
      const credential = await signInWithPopup(firebaseAuth, googleProvider);
      const resolvedAppUser = await exchangeIdToken(credential.user, credential.user.emailVerified);
      setUser(mapFirebaseUser(credential.user));
      setAppUser(resolvedAppUser);
    } finally {
      isSignInActionPending.current = false;
    }
  };

  const signInWithPassword = async (email: string, password: string): Promise<void> => {
    isSignInActionPending.current = true;
    try {
      const credential = await signInWithEmailAndPassword(firebaseAuth, email, password);
      const resolvedAppUser = await exchangeIdToken(credential.user, credential.user.emailVerified);
      setUser(mapFirebaseUser(credential.user));
      setAppUser(resolvedAppUser);
    } finally {
      isSignInActionPending.current = false;
    }
  };

  const signUpWithPassword = async (
    email: string,
    password: string,
    displayName: string
  ): Promise<void> => {
    isSignInActionPending.current = true;
    try {
      const credential = await createUserWithEmailAndPassword(firebaseAuth, email, password);
      if (displayName) {
        await updateProfile(credential.user, { displayName });
      }
      // Send verification email immediately. The action URL directs the user
      // back to /auth/action where the app handles the oobCode client-side.
      const actionCodeSettings: ActionCodeSettings = {
        url: `${window.location.origin}/auth/action`,
        handleCodeInApp: true,
      };
      await sendEmailVerification(credential.user, actionCodeSettings);

      // Explicitly sync the new user to the Protoss database now, rather than
      // waiting for onAuthStateChanged to do it asynchronously. onAuthStateChanged
      // fires as soon as createUserWithEmailAndPassword resolves (before
      // updateProfile and sendEmailVerification complete), so there is a window
      // where the token may not yet include the display name and the async
      // callback can complete silently with null. Calling exchangeIdToken here
      // guarantees the DB row is created before we navigate away.
      //
      // Force-refresh the token so backend claims include the updated displayName.
      const resolvedAppUser = await exchangeIdToken(credential.user, true);
      setUser(mapFirebaseUser(credential.user));
      setAppUser(resolvedAppUser);
    } finally {
      isSignInActionPending.current = false;
    }
  };

  const resendVerificationEmail = async (): Promise<void> => {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) throw new Error('NO_USER');
    // Skip if the email is already verified - this applies to Google-authenticated
    // users whose email is verified by the identity provider. Sending a
    // verification email to an already-verified address is a no-op at best and
    // confusing at worst.
    if (currentUser.emailVerified) return;
    const actionCodeSettings: ActionCodeSettings = {
      url: `${window.location.origin}/auth/action`,
      handleCodeInApp: true,
    };
    await sendEmailVerification(currentUser, actionCodeSettings);
  };

  const verifyEmail = async (oobCode: string): Promise<void> => {
    // Apply the action code - marks the email as verified in Firebase.
    await applyActionCode(firebaseAuth, oobCode);

    // If the user is currently signed in, reload their profile so the
    // emailVerified flag updates, then call the dedicated sync endpoint
    // so the Protoss database updates email_verified and email_verified_at.
    const currentUser = firebaseAuth.currentUser;
    if (currentUser) {
      await currentUser.reload();
      const resolvedAppUser = await syncVerifiedState(currentUser);
      setUser(mapFirebaseUser(currentUser));
      setAppUser(resolvedAppUser);
    }
    // If the user is not signed in (different device / browser), the
    // backend sync will happen automatically on their next sign-in via
    // onAuthStateChanged → exchangeIdToken (which also syncs verification state).
  };

  const sendPasswordReset = async (email: string): Promise<void> => {
    // continueUrl is this origin's /reset-password. The email link first hits the
    // Firebase Console action URL (/auth/action on one host per env), and
    // EmailActionPage forwards resetPassword links to continueUrl + oobCode, so the
    // user lands back on the workspace host they started from.
    const actionCodeSettings: ActionCodeSettings = {
      url: `${window.location.origin}${withDevWorkspaceParam('/reset-password')}`,
      handleCodeInApp: true,
    };
    await sendPasswordResetEmail(firebaseAuth, email, actionCodeSettings);
  };

  const resetPassword = async (oobCode: string, newPassword: string): Promise<void> => {
    await confirmPasswordReset(firebaseAuth, oobCode, newPassword);
  };

  const signOut = async (): Promise<void> => {
    await firebaseSignOut(firebaseAuth);
    // onAuthStateChanged will clear user and appUser
  };

  const value: AuthContextValue = {
    user,
    appUser,
    isAuthenticated: user !== null && appUser !== null,
    isLoading,
    signInWithGoogle,
    signInWithPassword,
    signUpWithPassword,
    sendPasswordReset,
    resetPassword,
    signOut,
    resendVerificationEmail,
    verifyEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth
 *
 * Hook for accessing auth state and actions.
 * Must be used inside <AuthProvider>.
 */
export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
